import { Box, Image } from "@chakra-ui/react";
import { getCdnImageSrc } from "goi_common";
import { BaseStack, baseColors } from "goi_common/src/design-system";

import type { ParlorOption } from "@/types/parlorOption";

const DEFAULT_IMAGE = getCdnImageSrc("platform/parlor/parlor-img-default-2.png");

/** 겹쳐 세울 사진 최대 장수. 넘는 만큼은 +N 뱃지로 줄인다 */
const MAX_THUMBS = 3;

/** 이름을 그대로 적을 최대 곳 수. 넘는 만큼은 "외 N곳" 으로 줄인다 */
const MAX_NAMES = 2;

/** 사진 한 장 크기와 겹칠 때 밀리는 간격 */
const THUMB_SIZE = 48;
const THUMB_STEP = 8;

/**
 * 전환 화면에서 방금 고른 장례식장을 되짚는 줄.
 *
 * 왜 사진을 쓰는가
 * - 이 퍼널의 진입 의도가 "장례식장을 찾고싶어요" 다. 고른 것이 이름 한 줄로만 남으면 방금
 *   사진을 보고 고른 행위와 이어지지 않는다. 같은 사진을 다시 보여줘야 "내가 고른 그 곳" 이 된다.
 * - 아이콘 하나(ConditionChip)로 먼저 만들었다가 바꿨다. 아이콘은 "장례식장"이라는 분류를
 *   말할 뿐 어디를 골랐는지는 말하지 못한다.
 *
 * 왜 사진 장수와 이름 개수가 다른가
 * - 사진은 3장까지, 이름은 2곳까지다. 사진은 겹쳐 세워서 3장이 들어가지만, 장례식장 이름은
 *   괄호 병기가 흔해 길어서 두 곳만 적어도 줄이 찬다. 각자 들어가는 만큼만 보여주고 남은 수를
 *   따로 줄인다.
 *
 * 배경 없이 위아래 구분선만 두는 이유
 * - 위의 체크 항목 3개와 아래 후기 블록 사이에 낀다. 후기가 이미 회색 배경 덩어리라 여기에도
 *   배경을 주면 덩어리가 둘 연속으로 붙어 어느 쪽이 무슨 말인지 흐려진다.
 */
export function MubinsoPickedParlorsRecap({ parlors }: { parlors: ParlorOption[] }) {
  if (!parlors.length) return null;

  const thumbs = parlors.slice(0, MAX_THUMBS);
  const hiddenThumbs = parlors.length - thumbs.length;
  const names = parlors
    .slice(0, MAX_NAMES)
    .map((parlor) => parlor.name)
    .join(", ");
  const restCount = parlors.length - Math.min(parlors.length, MAX_NAMES);

  return (
    <BaseStack
      direction="horizontal"
      width="100%"
      alignItems="center"
      spacing={16}
      padding="20px 0"
      borderTop={`1px solid ${baseColors.line_normal}`}
      borderBottom={`1px solid ${baseColors.line_normal}`}
    >
      <Box
        position="relative"
        flexShrink={0}
        width={`${THUMB_SIZE + THUMB_STEP * (MAX_THUMBS - 1)}px`}
        height={`${THUMB_SIZE + THUMB_STEP * (MAX_THUMBS - 1)}px`}
      >
        {thumbs.map((parlor, index) => (
          <Image
            key={parlor.id}
            src={parlor.imageUrl || DEFAULT_IMAGE}
            fallbackSrc={DEFAULT_IMAGE}
            alt=""
            position="absolute"
            /* 앞장이 왼쪽 아래, 뒷장일수록 오른쪽 위로 밀린다 */
            left={`${index * THUMB_STEP}px`}
            bottom={`${index * THUMB_STEP}px`}
            zIndex={thumbs.length - index}
            width={`${THUMB_SIZE}px`}
            height={`${THUMB_SIZE}px`}
            borderRadius="8px"
            objectFit="cover"
            // 사진끼리 붙으면 경계가 사라져 몇 장인지 안 보인다
            border={`2px solid ${baseColors.background_normal}`}
          />
        ))}

        {hiddenThumbs > 0 && (
          <Box
            position="absolute"
            left="-2px"
            bottom="-2px"
            zIndex={MAX_THUMBS + 1}
            minWidth="26px"
            height="22px"
            padding="0 6px"
            borderRadius="8px"
            backgroundColor={baseColors.label_normal}
            color={baseColors.background_normal}
            display="flex"
            alignItems="center"
            justifyContent="center"
            className="caption_1_sb"
            fontWeight={700}
          >
            +{hiddenThumbs}
          </Box>
        )}
      </Box>

      <BaseStack flex={1} minW={0} spacing={6}>
        <Box className="label_2_sb" color={baseColors.label_assistive_1} fontWeight={700}>
          선택 장례식장 {parlors.length}곳
        </Box>
        <Box
          className="headline_3_sb"
          color={baseColors.label_normal}
          fontWeight={700}
          wordBreak="keep-all"
          lineHeight="1.35"
        >
          {restCount ? `${names} 외 ${restCount}곳` : names}
        </Box>
      </BaseStack>
    </BaseStack>
  );
}
